import { Map, MapEvent } from '@vis.gl/react-google-maps';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { Region } from 'react-native-maps';

import { useGetUserImpactMap } from '@/api/hooks/donationHooks';
import { Impact as ImpactType } from '@/api/models/impactModels';
import { bs_icon } from '@/assets/images';
import useGetLocation from '@/hooks/useGetLocation';
import { firebaseImage, safeJsonParseArray, ThumbSize } from '@/utils/firebaseImage';
import { getMapCorners } from '@/utils/getMapCorners';

import DotsLoader from '../DotsLoader';
import ImpactMarkerWeb from './ImpactMarkerWeb';

interface ImpactMapWebProps {
  showDonation: (item: ImpactType) => void;
}

const ImpactMapWeb = ({ showDonation }: ImpactMapWebProps) => {
  const { mutateAsync: getMapData } = useGetUserImpactMap();

  const [donations, setDonations] = useState<ImpactType[]>([]);

  const location = useGetLocation();

  // Create an initialRegion based on user's location
  const initialRegion: Region = {
    latitude: location?.latitude ?? 37.0902, //USA center
    longitude: location?.longitude ?? -95.7129, //USA center
    latitudeDelta: 100,
    longitudeDelta: 100
  };

  useEffect(() => {
    getMapData(
      { polygon: getMapCorners(initialRegion) },
      {
        onSuccess: (response) =>
          setDonations(
            response.map<ImpactType>((donation, index) => ({
              id: index.toString(),
              amount: donation.npoTotalAmount,
              organization: donation.npoName,
              thumbnail:
                donation.thumbnail !== undefined
                  ? firebaseImage(safeJsonParseArray(donation.thumbnail ?? '[]')?.at(0), ThumbSize.Small)
                  : bs_icon,
              latitude: donation.location.coordinates[0],
              longitude: donation.location.coordinates[1]
            }))
          )
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRegionChanged = (e: MapEvent) => {
    'worklet';
    const region: Region = {
      latitude: e.map.getCenter()?.lat() ?? 37.0902,
      longitude: e.map.getCenter()?.lng() ?? -95.7129,
      latitudeDelta: 100,
      longitudeDelta: 100
    };
    getMapData(
      { polygon: getMapCorners(region) },
      {
        onSuccess: (response) =>
          setDonations(
            response.map<ImpactType>((donation, index) => ({
              id: index.toString(),
              amount: donation.npoTotalAmount,
              organization: donation.npoName,
              thumbnail:
                donation.thumbnail !== undefined
                  ? firebaseImage(safeJsonParseArray(donation.thumbnail ?? '[]')?.at(0), ThumbSize.Small)
                  : bs_icon,
              latitude: donation.location.coordinates[0],
              longitude: donation.location.coordinates[1]
            }))
          )
      }
    );
  };

  if (typeof window === 'undefined') {
    return (
      <View style={styles.mapLoader}>
        <DotsLoader />
      </View>
    );
  }

  return (
    <Map
      mapId={'BS_IMPACT_MAP'}
      style={styles.map}
      defaultCenter={{
        lat: location?.latitude ?? 37.0902, //USA center
        lng: location?.longitude ?? -95.7129 //USA center
      }}
      defaultZoom={3}
      onDragend={onRegionChanged}
      onZoomChanged={onRegionChanged}>
      {donations.map((item) => {
        return <ImpactMarkerWeb key={item.id} data={item} showDonation={() => showDonation(item)} />;
      })}
    </Map>
  );
};

const styles = StyleSheet.create({
  mapLoader: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  },

  map: {
    marginBottom: 25,
    flex: 1
  }
});

export default ImpactMapWeb;
